import React, { useEffect, useMemo, useRef, useState } from 'react';
import { reduxRootTypes } from 'src/types';
import { useDispatch, useSelector } from 'react-redux';

// #region Leaflet related imports
import L, { Marker } from 'leaflet';
import 'leaflet-switch-basemap/src/L.switchBasemap';
import 'leaflet-switch-basemap/src/L.switchBasemap.css';
import 'leaflet-edgebuffer/src/leaflet.edgebuffer';
import 'leaflet/dist/leaflet.css';
import 'src/libTypes/leaflet-minimap.d';
import 'leaflet-minimap';
import 'leaflet-contextmenu/dist/leaflet.contextmenu';
import 'leaflet-contextmenu/dist/leaflet.contextmenu.css';
import 'leaflet-easyprint/dist/bundle';
// @ts-ignore
import terminator from '@joergdietrich/leaflet.terminator';

import icon from 'src/libs/Leaflet.draw/images/marker-icon.png';
import 'src/app/components/MapComponent/leaflet-icon-fix';

// #endregion

import _, { max, min } from 'lodash';

// #region Styling imports
import styles from 'src/app/components/MapComponent/MapComponent.module.scss';
import './map.scss';
// #endregion

import { contentActions, mapActions, unitsActions } from 'src/app/redux/actions';

import { MapMarkerController, MapVisualController, UnitsController } from 'src/controllers';

import { DrawController } from 'src/controllers/DrawController';
import { hasPart, toBinaryString } from 'src/app/helpers/binaries/operations';
import { SitrepSelectedType, TBarPosition } from 'src/types/mixed.type';
import 'react-image-crop/dist/ReactCrop.css';

import { getAgentName } from 'src/app/helpers/units.helper';
import Toolbox from '../Toolbox/Toolbox';
import { EteMapExp } from './plugins/EteMapExport';
import {
  getSenseInfo,
  getWeaponRanges,
  mapBoundaries,
  MapPropsType,
  OverlappedIconAttributesType,
  OverlappedUnitsPopupArgs,
  RulerPopupArgs,
  TDetectionAngleType,
} from './map.config';
import DraggableModal from '../DraggableModal/DraggableModal';
import { usePrevious } from 'src/app/hooks/UsePreviousHook';
import { MapViewState } from 'src/enums/map.enum';

import { LayersController } from 'src/app/controllers';
import MapCropper from './MapExtraComponents/MapCropper';
import OverlappedUnits from './OverlappedUnits/OverlappedUnits';
import {
  tmkAlgiYapabilirMi,
  tmkIsSensor,
  tmkIsWeapon,
  tmkKarakteristikUnnamed4,
} from 'src/app/helpers/binaries/preset-queries.binary';
import { EEtmenTipi } from 'src/types/components/socket.type';
import { DrawenLayerGroupResultType } from 'src/types/components/draw-module.type';
import { UnitType } from 'src/types/unit.type';
import message from 'antd/es/message';
import { preloadMilsymbol } from 'src/app/helpers/milSymbol.helper';

// Controller to on/off time polygon filter
let timePolygonController: any;

// Set default marker for icon for leaflet because it doesn't work on its own
L.Marker.prototype.options.icon = L.icon({
  iconUrl: icon,
  iconSize: [25, 41],
});

let mapExportPlugin: undefined | EteMapExp;

const MapComponent: React.FC<MapPropsType> = ({
  mapOptions,
  mapViewState,
  mapInputProps,
  unitsSelected,
  removeUnitMarkersOnMap,
  localTasks,
  isTaskDropdownOpen = false,
}) => {
  const dispatch = useDispatch();

  const { unitToAdd, unitsToDelete, sitrepSelectedUnits, extraBars, chSelectedUnit } = useSelector(
    (state: reduxRootTypes.RootStateType) => state.unitsState,
  );
  const {
    activeTabs,
    activeLayer,
    cropMap,
    mapZoom,
    mapLastCenter,
    availableLayers,
    mapVisualSettings,
    unitWeaponRange,
  } = useSelector((state: reduxRootTypes.RootStateType) => state.mapState);

  const { systems, filteredSystems } = useSelector((state: reduxRootTypes.RootStateType) => state.contentState);
  const { units } = useSelector((state: reduxRootTypes.RootStateType) => state.unitsState);

  // Map component for Leafet.Map
  const [map, setMap] = useState<L.Map | undefined>(undefined); // alınacak

  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [mapVisualController, setMapVisualController] = useState<MapVisualController | undefined>(undefined); // alınacak
  const [unitsController, setUnitsController] = useState<UnitsController | undefined>(undefined); // alınacak
  const [mapMarkerController, setMapMarkerController] = useState<MapMarkerController | undefined>(undefined); // alınacak
  const [drawController, setDrawController] = useState<DrawController | undefined>(undefined); // alınacak

  const [sitrepActiveUnits, setSitrepActiveUnits] = useState<SitrepSelectedType[]>([]); // alınacak
  const [layersController, setLayersController] = useState<LayersController | undefined>(undefined);
  const [anyLocalPendingRequest, setAnyLocalPendingRequest] = useState(false);

  const [controllersAreSet, setControllersAreSet] = useState(false); // alınacak
  const [drawGroups, setDrawGroups] = useState<DrawenLayerGroupResultType[]>([]);

  const [mapCropActive, setMapCropActive] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState('');

  const [overlappedUnitsPopupContent, setOverlappedUnitsPopunContent] = useState<
    | {
        icons: OverlappedIconAttributesType[];
        markers: L.Marker<any>[];
        mouseEvent: L.LeafletEvent;
        position: TBarPosition | undefined;
      }
    | undefined
  >(undefined); // alınacak

  const unitsInUse = useMemo(() => {
    return unitsSelected || [];
  }, [mapViewState, unitsSelected]);

  useEffect(() => {
    const loadMilSymbol = async () => {
      await preloadMilsymbol();
    };

    loadMilSymbol();
  }, []);

  const rulerPopup = ({ bearing, distance, duration, position }: RulerPopupArgs) => {
    message.info({
      className: 'ruler-popup',
      // content: () => { return (<span>Distance Between Points: ${distance} meters</span><br /><span>Bearing Between Points: ${br} degree</span>)},
      content: (
        <div className="ruler-popup-wrapper">
          <span>
            Distance Between Points: <b>{distance}</b> meters
          </span>
          <span>
            Bearing Between Points: <b>{bearing}</b> degree
          </span>
        </div>
      ),
      icon: undefined,
      style: {
        position: 'absolute',
        top: position.y + 18,
        left: position.x + 24 + 150,
        zIndex: '13123213123',
      },
      duration,
    });
  };
  const calculateOverlappedUnitsPopup = (x: number, y: number, markersLength: number) => {
    const posX: number = x - 640;
    let posY: number = 0;

    if (y > 450) {
      if (markersLength < 18) {
        const multiplier = markersLength / 2;
        posY = y - 80 * multiplier;
      } else {
        posY = y - 400;
      }
    } else {
      posY = y - 40;
    }

    return {
      x: posX,
      y: posY,
    };
  };

  // // Render overlapped unitsSelected popup as antd message
  // const overlappedUnitsPopup = ({ markers, event, icons }: OverlappedUnitsPopupArgs) => {
  //   setOverlappedUnitsPopunContent(undefined);
  //   const rect = document.getElementsByClassName(`unit-icon-${markers[0].options.unitId}`)[0].getBoundingClientRect();
  //   if (rect) {
  //     setOverlappedUnitsPopunContent({
  //       icons,
  //       markers,
  //       mouseEvent: event,
  //       // position: { x: rect.x - 640, y: rect.y - 40 },
  //       position: calculateOverlappedUnitsPopup(rect.x, rect.y, markers.length),
  //     });
  //   } else {
  //     setOverlappedUnitsPopunContent(undefined);
  //   }
  // }; // alınacak

  /**
   * Section that only works on first mount.
   * Resets active tabs for toolbar, sets Map object for Leaflet,
   * sets extra plugins and controls if necessary.
   */
  useEffect(() => {
    dispatch(mapActions.resetActiveTabs());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setOverlappedUnitsPopunContent(undefined);
      }
    });

    if (map === undefined) {
      const canvas = L.canvas();

      // Initialize map object
      const mapObject = L.map('map', {
        ...mapOptions,
        zoomControl: false,
        renderer: canvas,
      });

      mapRef.current = mapObject;

      mapObject.createPane('custom-conic-pane');

      const customConicPane = mapObject.getPane('custom-conic-pane');

      if (customConicPane) {
        customConicPane.style.opacity = '0.4';
      }

      canvas.off('contextmenu');

      try {
        canvas.on('add', () => {
          // @ts-ignore
          canvas._container.oncontextmenu = () => {
            return false;
          };
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to disable contentmenu for canvas');
      }

      // // Set Scale control display and add to map
      L.control.scale({ position: 'bottomright' }).addTo(mapObject);

      mapExportPlugin = new EteMapExp({
        position: 'topleft',
        setCropActive: () => setMapCropActive(true),
        setImageCropUrl: (url: string) => setCropImageUrl(url),
        canExportSitrepMisrep: mapViewState === MapViewState.Default,
      });
      mapExportPlugin.addTo(mapObject);

      // @ts-ignore
      timePolygonController = terminator();

      setMap(mapObject);
    }
  }, []); // alınacak

  useEffect(() => {
    sitrepSelectedUnits.forEach((s) => {
      const elem = document.getElementsByClassName(`unit-icon-${s.id}`)[0];
      // elem?.classList.add('sitret-selected-unit-determiner');
      if (elem) elem.id = 'id-selected-unit-determiner';
    });
  }, [sitrepSelectedUnits, unitsInUse, map?.getBounds()]);
  /**
   * On Unit selected for SITREP, travers TMK and Muhimmat data related to the Unit and
   * finds, min max ranges for engagmeent and sense.
   * Then passes to MapMarkerController to draw as L.Donut's
   */

  useEffect(() => {
    if (mapMarkerController) {
      const sitrepRemovedList = sitrepActiveUnits.filter(
        ({ id: activeUnitId }) => !sitrepSelectedUnits.some(({ id: unitId }) => unitId === activeUnitId),
      );

      sitrepRemovedList.forEach(({ id: sitrepRemoved }) => {
        mapMarkerController.onSitrepSelectUnit(sitrepRemoved, false, undefined, undefined, undefined, false);
      });

      sitrepSelectedUnits.forEach(({ id: unitId, sensingRangeActive, volumeActive, weaponRangeActive }) => {
        const sitrepSelectedUnit = unitsSelected.find((o) => o.id === unitId && o.algiDurumu > 0);

        if (sitrepSelectedUnit) {
          let maxRanges: number[] = [];
          let minRanges: number[] = [];

          let maxIdentificationRanges: number[] = [];
          let maxDetectionRanges: number[] = [];
          let detectionAngles: TDetectionAngleType[] = [];

          if (sitrepSelectedUnit.etmenTipi === EEtmenTipi.DENIZ_MAYINI) {
            return;
          }

          if (sitrepSelectedUnit.systemStatus)
            sitrepSelectedUnit.systemStatus.forEach((o) => {
              let tmk: any;
              if (
                filteredSystems.length > 0 &&
                filteredSystems.some((system) => system.unitId === sitrepSelectedUnit.id)
              ) {
                tmk = filteredSystems.find(
                  (system) => system.tmk.id === o.tmkId && system.unitId === sitrepSelectedUnit.id,
                )?.tmk;
              } else {
                tmk = systems.find((system) => system.id === o.tmkId);
              }

              if (tmk) {
                if (weaponRangeActive && tmkIsWeapon(tmk)) {
                  const [calculatedMinRanges, calculatedMaxRanges] = getWeaponRanges(tmk);

                  minRanges = minRanges.concat(calculatedMinRanges);
                  maxRanges = maxRanges.concat(calculatedMaxRanges);
                }

                if (sensingRangeActive) {
                  if (tmkAlgiYapabilirMi(tmk)) {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    const senseInfo = getSenseInfo(tmk);
                    maxIdentificationRanges = maxIdentificationRanges.concat(senseInfo.maxIdentificationRanges);
                    maxDetectionRanges = maxDetectionRanges.concat(senseInfo.maxDetectionRanges);
                    detectionAngles = detectionAngles.concat(senseInfo.detectionAngles);
                  }
                }
              }
            });

          mapMarkerController.onSitrepSelectUnit(
            sitrepSelectedUnit.id,
            activeTabs.sitrep,
            {
              range: weaponRangeActive
                ? maxRanges.length > 0
                  ? {
                      radius: max(maxRanges) || 1,
                      innerRadius: min(minRanges) || 0,
                    }
                  : undefined
                : undefined,
              sense: sensingRangeActive
                ? maxDetectionRanges.length > 0 && maxIdentificationRanges.length > 0
                  ? {
                      detectionAngle: detectionAngles || [],
                      detectionRadius: max(maxDetectionRanges) || 1,
                      identificationRadius: max(maxIdentificationRanges) || 1,
                    }
                  : undefined
                : undefined,
            },
            volumeActive
              ? {
                  bearing: sitrepSelectedUnit.istikamet,
                  center: sitrepSelectedUnit.konum,
                  volume: sitrepSelectedUnit.hacim,
                }
              : undefined,
            undefined,
            false,
          );
        }
      });

      setSitrepActiveUnits(sitrepSelectedUnits);

      return () => {
        sitrepSelectedUnits.forEach(({ id }) => {
          const foundedSelectedUnit = unitsSelected.find((o) => o.id === id);

          if (foundedSelectedUnit) {
            mapMarkerController.onSitrepSelectUnit(
              foundedSelectedUnit?.id,
              false,
              undefined,
              undefined,
              undefined,
              false,
            );
          }
        });
      };
    }
  }, [sitrepSelectedUnits, mapMarkerController, activeTabs.sitrep, filteredSystems, unitWeaponRange?.isOpen]);

  /**
   * Callback function to handle actions on contextmenu event of Unit Markers.
   * Being used to give quick commands to unitsSelected, filtered related to their domains by looking at their SIDC series.
   *
   * @param commandType Domain of the command (Land, Air, Maritime etc.)
   * @param commandId Unique Id for the command to determine the type of the form to be displayed
   * @param unitId Id of the unit clicked on contextmenu action
   */

  /**
   * Function that triggers 3D integration. Calls custom protocol for 3D executable and passes Id of the unit.
   * Used as callback function for contextmenu on unit markers
   *
   * @param id Id of the unit to be displayed at 3D
   */
  const onMarkerViewInThreed = (id: number, lat: number, lng: number) => {
    const win = window.open(`hymots3d://${id}_${lat}_${lng}`, '_blank');
  };

  const onMapZoom = (zoomLevel: number, coords: L.LatLng) => {
    dispatch(mapActions.setMapZoom(zoomLevel));
    dispatch(mapActions.setMaplastCenter(coords));
  }; // alınacak

  const onMapDrag = (coords: L.LatLng) => {
    dispatch(mapActions.setMaplastCenter(coords));
  }; // alınacak

  const onMarketRightClickCancelExecutedCommand = (unitId: number) => {
    // setCancelExecutedCommandClickedUnitId(unitId);
  };

  const onMarkerRightClickEtmenTamamla = (unitId: number) => {
    // commandHelper.etmenTamamla(gameId, unitId);
  };

  const prevSitrepSelectedUnitsState = usePrevious(sitrepSelectedUnits);
  const prevExtraBarsState = usePrevious(extraBars);

  // Render misrep or cq if its active
  useEffect(() => {
    if (sitrepSelectedUnits?.length) {
      if (prevSitrepSelectedUnitsState?.length < sitrepSelectedUnits.length) {
        if (activeTabs.misrep && sitrepSelectedUnits[0]?.id) {
          dispatch(unitsActions.misrepSelectUnit(sitrepSelectedUnits[0]?.id));
        }
        if (activeTabs.commandQueue && sitrepSelectedUnits[0]?.id) {
          dispatch(unitsActions.commandQueueSelectUnit(sitrepSelectedUnits[0]?.id));
        }
      }
    }
    if (extraBars?.length) {
      // if (prevExtraBarsState?.length < sitrepSelectedUnits?.length) {
      if (activeTabs.misrep && extraBars[0]) {
        dispatch(unitsActions.misrepSelectUnit(extraBars[0]));
      }
      if (activeTabs.commandQueue && extraBars[0]) {
        dispatch(unitsActions.commandQueueSelectUnit(extraBars[0]));
      }
      // }
    }
  }, [sitrepSelectedUnits, extraBars]);

  useEffect(() => {
    if (mapMarkerController && chSelectedUnit?.id !== undefined) {
      if (mapMarkerController && chSelectedUnit?.id !== undefined) {
        mapMarkerController.zoomToUnit(chSelectedUnit.id);
      }
    }
    return () => {
      dispatch(unitsActions.chSelectUnit(undefined));
    };
  }, [dispatch, chSelectedUnit, mapMarkerController]);

  // Reset sitrep, misrep and cq bars
  useEffect(() => {
    if (!activeTabs.sitrep) {
      dispatch(unitsActions.resetSitrepSelectedUnits());
    }
  }, [activeTabs.sitrep]); // alınacak

  const onEditModeLayerAdded = (layer: L.Layer) => {};

  const onEditModelDeleteLayers = () => {};

  useEffect(() => {
    if (map) {
      if (!drawController) {
        const localDrawController = new DrawController(map);
        setDrawController(localDrawController);
      } else if (drawController) {
        drawController.removeFromMap();
        setDrawController(undefined);
      }
    }
  }, [map]);

  /**
   * Sets controllers, depending on theater mode
  //  */
  useEffect(() => {
    const setLayers = async () => {
      if (map !== undefined && mapViewState && !controllersAreSet) {
        if (mapViewState === MapViewState.Default) {
          const localMapVisualController = new MapVisualController(map, false, mapViewState, rulerPopup);
          const localUnitsController = new UnitsController();
          const localMapMarkerController = new MapMarkerController(
            map,
            false,
            mapViewState,
            localUnitsController,
            // userRole,
            {
              onMapUnitClicked:
                /**
                 * Callback function that handles on mouse left click event on the Units.
                 * Sets selected unit's id for the SITREP.
                 * And toggles visiblity for SITREP panel.
                 *
                 * @param unitId id for the selected map unit
                 */
                (unitId: number | bigint, unitType: UnitType) => {
                  dispatch(unitsActions.sitrepSelectUnit(unitId, unitType));
                  dispatch(mapActions.setActiveTab({ key: 'sitrep', bool: true }));
                },
              // onMarkerRightClickCommand: onMapUnitCommandClicked,
              onMarkerViewInThreed,
              onMarketRightClickCancelExecutedCommand,
              onMapZoom,
              onMapDrag,
              onMarkerRightClickEtmenTamamla,
              // onMarkerUnitSizeChange: () => setMarkerSizeChanged(true),
            },
          );

          localUnitsController.setMarkerController(localMapMarkerController);

          const theaterBounds = L.latLngBounds(Object.values(mapBoundaries));

          setMapVisualController(localMapVisualController);
          setUnitsController(localUnitsController);
          setMapMarkerController(localMapMarkerController);

          localMapVisualController.setMapMarkerController(localMapMarkerController);
          const localLayersController = LayersController.getInstance();
          setLayersController(localLayersController);

          setControllersAreSet(true);
        }
      }
    };

    setAnyLocalPendingRequest(true);
    setLayers();
  }, [map, mapViewState]); // alınacak

  useEffect(() => {
    if (layersController && availableLayers.length && map) {
      layersController.setLayersSource(availableLayers, map);
    }
  }, [layersController, availableLayers]);

  /**
   * Hook, on active layer changed, set main layer and minimap layer
   */
  useEffect(() => {
    if (map && layersController && activeLayer) {
      layersController.handleLayerChange(activeLayer, map);
    }
  }, [activeLayer, map, layersController]);

  useEffect(() => {
    if (unitsController && unitToAdd) {
      unitsController.addUnit(unitToAdd);
      dispatch(unitsActions.addUnit(undefined));
    }
  }, [unitsController, unitToAdd]); // alınacak

  useEffect(() => {
    if (mapMarkerController && unitsToDelete) {
      mapMarkerController.deleteUnits(unitsToDelete);

      const updatedUnits = units.filter((o) => o.id !== unitsToDelete.find((u) => u === o.id));

      dispatch(unitsActions.setUnits(updatedUnits));
    }
  }, [mapMarkerController, unitsToDelete?.length]); // alınacak

  useEffect(() => {
    if (mapMarkerController && removeUnitMarkersOnMap && removeUnitMarkersOnMap?.length > 0) {
      mapMarkerController.deleteUnits(removeUnitMarkersOnMap);
    }
  }, [removeUnitMarkersOnMap]);

  useEffect(() => {
    if (unitsController && mapMarkerController) {
      if (unitsSelected.length === 0) {
        mapMarkerController.clearUnitMarkers();
      } else {
        isTaskDropdownOpen && localTasks && localTasks?.length > 0 && mapMarkerController.clearUnitMarkers();

        unitsController.loadUnits(unitsSelected);
      }
    }
  }, [unitsController, unitsSelected, mapMarkerController]);

  // const onClickOverlappedUnit = (id: number) => {
  //   dispatch(unitsActions.sitrepSelectUnit(id));
  //   dispatch(mapActions.setActiveTab({ key: 'sitrep', bool: true }));
  // }; // alınacak

  const onRightClickOverlappedUnit = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, id: number) => {
    e.preventDefault();
    if (overlappedUnitsPopupContent) {
      const findedMarker = overlappedUnitsPopupContent.markers.find((o) => o.options.unitId === id);
      findedMarker?.fire('contextmenu', overlappedUnitsPopupContent.mouseEvent, true);
    }
  };

  useEffect(() => {
    if (!containerRef.current || !mapRef.current) return;

    const observer = new ResizeObserver(() => {
      mapRef.current?.invalidateSize();
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.mainContainer}>
      <div className={styles.mapContainer} ref={containerRef}>
        <div id="map" className={styles.map} />

        <MapCropper active={mapCropActive} setActive={setMapCropActive} imageUrl={cropImageUrl} />
        <OverlappedUnits mapMarkerController={mapMarkerController} />

        <Toolbox
          mapViewState={mapViewState}
          map={map}
          mapVisualController={mapVisualController}
          mapMarkerController={mapMarkerController}
          drawController={drawController}
          setDrawGroups={setDrawGroups}
          drawGroups={drawGroups}
        />
      </div>
    </div>
  );
};

MapComponent.defaultProps = {
  mapOptions: {
    center: [50, -5],
    zoom: 2.5,
    minZoom: 2.5,
    maxZoom: 18,
    maxBounds: [
      [-85, -250],
      [85, 250],
    ],
    maxBoundsViscosity: 0.5,
    attributionControl: false,
    worldCopyJump: false,
    wheelPxPerZoomLevel: 200,
    zoomDelta: 0.25,
    zoomSnap: 0.25,
  },
  // mapViewState: MapViewState.Default,
};

export default MapComponent;
